'use client'

import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { CheckCircle2, CircleCheck } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { StoryboardSegmentPreviewEntry } from '../types'
import useStoryboardMediaSource from '../hooks/useStoryboardMediaSource'

export interface PreviewEntryCardProps {
  entry: StoryboardSegmentPreviewEntry;
  projectId: string;
  segmentId: string;
  isFinal: boolean;
  isActive: boolean;
  sequenceNumber: number;
  mutating: boolean;
  onPreview: (entry: StoryboardSegmentPreviewEntry) => void;
  onSetFinal: (entry: StoryboardSegmentPreviewEntry) => Promise<void> | void;
  onOpenContextMenu?: (
    entry: StoryboardSegmentPreviewEntry,
    event: MouseEvent<HTMLElement>
  ) => void;
}

// 使用视频的第10帧作为缩略图基准。
const FRAME_PREVIEW_OFFSET = 10;
// 缺少真实帧率时按30fps估算偏移时间。
const FALLBACK_FPS = 30;

const PreviewEntryCard: React.FC<PreviewEntryCardProps> = ({
  entry,
  projectId,
  segmentId,
  isFinal,
  isActive,
  sequenceNumber,
  mutating,
  onPreview,
  onSetFinal,
  onOpenContextMenu,
}) => {
  const { src } = useStoryboardMediaSource({
    projectId,
    segmentId,
    entry,
    autoFetch: isActive,
  });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPreviewReady, setIsPreviewReady] = useState(false);

  useEffect(() => {
    const element = videoRef.current;
    if (!element || !src) {
      setIsPreviewReady(false);
      return;
    }
    const videoElement: HTMLVideoElement = element;
    let cancelled = false;

    const handleReady = () => {
      if (cancelled) return;
      videoElement.pause();
      setIsPreviewReady(true);
    };

    const handleSeeked: EventListener = () => {
      removeListeners();
      handleReady();
    };

    const handleError: EventListener = () => {
      removeListeners();
      handleReady();
    };

    const handleLoadedMetadata: EventListener = () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      seekToOffset();
    };

    const removeListeners = () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('seeked', handleSeeked);
      videoElement.removeEventListener('error', handleError);
    };

    const seekToOffset = () => {
      if (cancelled) return;
      const hasFiniteDuration = Number.isFinite(videoElement.duration);
      const duration = hasFiniteDuration ? videoElement.duration : undefined;
      const epsilon = 1 / FALLBACK_FPS;
      const frameTime = FRAME_PREVIEW_OFFSET / FALLBACK_FPS;
      const maxTime = duration !== undefined ? Math.max(duration - epsilon, 0) : undefined;
      const targetTime = maxTime !== undefined ? Math.min(frameTime, maxTime) : frameTime;

      videoElement.pause();
      videoElement.removeEventListener('seeked', handleSeeked);
      videoElement.addEventListener('seeked', handleSeeked);

      try {
        videoElement.currentTime = targetTime;
      } catch {
        handleError(new Event('error'));
      }
    };

    setIsPreviewReady(false);
    videoElement.addEventListener('error', handleError);

    if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekToOffset();
    } else {
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    }

    return () => {
      cancelled = true;
      removeListeners();
    };
  }, [src, entry.previewId]);

  return (
    <div
      key={entry.previewId}
      role="button"
      tabIndex={0}
      onClick={() => onPreview(entry)}
      onContextMenu={(event) => {
        if (!onOpenContextMenu) return
        event.preventDefault()
        event.stopPropagation()
        onOpenContextMenu(entry, event)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onPreview(entry)
        }
      }}
      className={[
        'flex-none rounded-2xl relative overflow-hidden bg-black cursor-pointer',
        'transition-transform duration-150 ease-in-out hover:-translate-y-0.5',
        'shadow-sm',
        isFinal
          ? 'ring-2 ring-primary border-0 shadow-md'
          : isActive
            ? 'ring-2 ring-sky-500/80'
            : 'border border-border',
      ].join(' ')}
      style={{ width: '220px' }}
    >
      <video
        ref={videoRef}
        src={src ?? entry.url ?? undefined}
        muted
        playsInline
        preload="metadata"
        style={{ width: '100%', height: 'auto', display: 'block', backgroundColor: '#000', opacity: isPreviewReady ? 1 : 0, transition: 'opacity 120ms ease-in-out' }}
      />
      <div className="absolute top-2 left-2 z-20 flex flex-col gap-1 items-start">
        <span className="px-1.5 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-semibold shadow-sm">
          第{sequenceNumber}次
        </span>
        {isActive ? (
          <span className="px-1.5 py-0.5 rounded-full bg-sky-500/90 text-white text-[11px] font-semibold shadow-sm">
            播放中
          </span>
        ) : null}
      </div>
      {isFinal ? (
        <div className="absolute inset-0 rounded-inherit pointer-events-none z-10 bg-gradient-to-br from-primary/35 to-transparent" />
      ) : null}
      <div className="absolute top-2 right-2 z-20">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={(event) => {
                event.stopPropagation()
                void onSetFinal(entry)
              }}
              disabled={isFinal || mutating}
              className={[
                'inline-flex items-center justify-center rounded-full border text-white shadow',
                'size-7 transition-colors',
                isFinal
                  ? 'bg-primary border-primary/60'
                  : 'bg-black/60 border-white/40 hover:bg-black/80',
                'disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100',
              ].join(' ')}
            >
              {isFinal ? <CheckCircle2 className="size-4" /> : <CircleCheck className="size-4" />}
            </button>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>
            {isFinal ? '当前已设为最终视频' : '设为最终视频'}
          </TooltipContent>
        </Tooltip>
      </div>
      {isFinal ? (
        <span className="absolute bottom-3 left-3 z-20 px-2 py-0.5 rounded-full bg-primary/90 text-primary-foreground text-[11px] font-semibold shadow">
          最终视频
        </span>
      ) : null}
    </div>
  )
};

export default PreviewEntryCard;
